// The Family shell's phone chrome (below `md`): a top bar and a bottom tab
// bar, like a native app. On `md` and up the sidebar does both jobs.
import { PersonAvatar, type PersonTint } from "@crew/ui/components/crew-avatar";
import { Link } from "@crew/ui/components/link";
import type { IconComponent } from "@crew/ui/components/nav-icons";
import { NewDot } from "@crew/ui/components/crew-sidebar";
import { CountBadge } from "@crew/ui/components/status";
import { cn } from "@crew/ui/lib/utils";
import type { ComponentProps, ReactNode } from "react";

export type CrewTab = {
  href: string;
  icon: IconComponent;
  label: string;
  active: boolean;
  badge?: number;
  /** A copper dot on the icon: something not seen yet under it (More's What's new). */
  dot?: boolean;
};

/**
 * Sticky top bar: menu, workspace name (a link to `titleHref`, when given),
 * then `actions` on the right, below the status area.
 */
export function CrewMobileTopBar({
  menu,
  title,
  titleHref,
  actions,
}: {
  menu: ReactNode;
  title: string;
  titleHref?: string;
  actions?: ReactNode;
}) {
  const titleClass = "min-w-0 flex-1 truncate font-display text-xl text-foreground";
  return (
    <header
      data-slot="crew-mobile-top-bar"
      className="sticky top-0 z-20 flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center gap-1 border-b border-border bg-sidebar pt-[env(safe-area-inset-top)] pr-[max(0.25rem,env(safe-area-inset-right))] pl-[max(0.25rem,env(safe-area-inset-left))] md:hidden"
    >
      {menu}
      {titleHref ? (
        <Link
          href={titleHref}
          className={cn(
            titleClass,
            "flex h-11 items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <span className="truncate">{title}</span>
        </Link>
      ) : (
        <span className={titleClass}>{title}</span>
      )}
      {actions ? <div className="flex items-center">{actions}</div> : null}
    </header>
  );
}

/** The signed-in person as a round avatar, sized for the top bar. */
export function CrewTopBarAvatar({ initials, tint }: { initials: string; tint: PersonTint }) {
  return <PersonAvatar initials={initials} tint={tint} size={32} />;
}

/**
 * Fixed bottom tab bar. It sits above the home indicator (safe area), and
 * the page leaves room for it with `pb-(--crew-tab-bar)`.
 */
export function CrewTabBar({ tabs, more }: { tabs: CrewTab[]; more?: ReactNode }) {
  return (
    <nav
      aria-label="Sections"
      data-slot="crew-tab-bar"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-sidebar pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <ul className="flex h-16 items-stretch">
        {tabs.map((tab) => (
          <li key={tab.href} className="flex flex-1">
            <Link
              href={tab.href}
              aria-current={tab.active ? "page" : undefined}
              className={tabClass(tab.active)}
            >
              <TabFace tab={tab} />
            </Link>
          </li>
        ))}
        {more ? <li className="flex flex-1">{more}</li> : null}
      </ul>
    </nav>
  );
}

/** A tab that opens something rather than going somewhere (the phone's More). */
export function CrewTabButton({
  tab,
  className,
  ...props
}: ComponentProps<"button"> & { tab: Omit<CrewTab, "href"> }) {
  return (
    <button type="button" className={cn(tabClass(tab.active), className)} {...props}>
      <TabFace tab={tab} />
    </button>
  );
}

function tabClass(active: boolean) {
  return cn(
    "relative flex flex-1 cursor-pointer flex-col items-center justify-center gap-1 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active ? "text-foreground" : "text-muted-foreground",
  );
}

/** A tab's icon, badge, label and active bar. */
function TabFace({ tab }: { tab: Omit<CrewTab, "href"> }) {
  return (
    <>
      <span className="relative">
        <tab.icon size={22} />
        {tab.badge ? (
          <CountBadge className="absolute -top-2 -right-3">{tab.badge}</CountBadge>
        ) : tab.dot ? (
          <NewDot className="absolute -top-0.5 -right-1 size-2 ring-2 ring-background" />
        ) : null}
      </span>
      {tab.label}
      {tab.active ? (
        <span className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-foreground" />
      ) : null}
    </>
  );
}
